import {
  RenderOptions,
  RenderResult,
  act,
  fireEvent,
  render,
} from '@testing-library/react';
import { jsdomReactUniDriver } from '@wix/unidriver-jsdom-react';
import {
  TestkitOutputRegular,
  TestkitOutputUni,
} from '@wix/wix-ui-test-utils/dist/types/testing-library';
import { BaseUniDriver } from '@wix/wix-ui-test-utils/dist/types/base-driver';
import { BaseDriver } from '@wix/wix-ui-test-utils';

type CreateRendererOptions = Partial<
  {
    dataHook: string;
  } & RenderOptions<any>
>;

type DriverFactoryWrapperArgs = {
  rendered: RenderResult<any, HTMLElement, HTMLElement>;
  element: Element | null;
  component: React.ReactNode;
  dataHook?: string;
};

type DriverFactoryWrapper<Driver> = (args: DriverFactoryWrapperArgs) => Driver;

const getElement = ({
  rendered,
  dataHook,
}: Pick<DriverFactoryWrapperArgs, 'rendered' | 'dataHook'>): Element | null => {
  const element = dataHook
    ? rendered.container.querySelector(`[data-hook="${dataHook}"]`)
    : rendered.container.firstElementChild;

  return element;
};

function createAsyncRendererBase<Driver>(
  createDriver: DriverFactoryWrapper<Driver>,
  defaultOptions: CreateRendererOptions,
) {
  return async (jsx: JSX.Element, options = defaultOptions) => {
    const { dataHook, ...reactTestingLibraryOptions } = {
      ...defaultOptions,
      ...options,
    };

    let renderResult: DriverFactoryWrapperArgs['rendered'];
    await act(async () => {
      renderResult = render(jsx, reactTestingLibraryOptions);
    });

    // non-null/undef assertion used here because renderResult is already assigned
    const rendered = renderResult!;
    const element = getElement({ rendered, dataHook });

    const actWrappedRerender = async (jsxElement: JSX.Element) => {
      await act(async () => {
        rendered.rerender(jsxElement);
      });
    };

    return {
      ...rendered,
      rerender: actWrappedRerender,
      driver: createDriver({ rendered, element, component: jsx, dataHook }),
    };
  };
}

function createRendererBase<Driver>(
  createDriver: DriverFactoryWrapper<Driver>,
  defaultOptions: CreateRendererOptions,
) {
  return (jsx: JSX.Element, options = defaultOptions) => {
    const { dataHook, ...reactTestingLibraryOptions } = {
      ...defaultOptions,
      ...options,
    };

    const rendered = render(jsx, reactTestingLibraryOptions);
    const element = getElement({ rendered, dataHook });
    return {
      ...rendered,
      driver: createDriver({ rendered, element, component: jsx, dataHook }),
    };
  };
}
/**
 * Creates a `render` function that returns the same object as `@testing-library/react`'s render, but
 * with and extra `driver` property. Designed for usage with legacy drivers. Deprecated.
 *
 * The returned render function arguments:
 * @param [React.Element] jsx a jsx element to render
 * @param [object] options - render-options for @testing-library/react. The options may also contain a `dataHook` prop which if provided then the driver would be created with the element which is found by the dataHook. If not provided then it assumes that the rendered root element is the component's root element and it will be used for the driver.
 */
export function createRendererWithDriver<Driver extends BaseDriver>(
  driverFactory: TestkitOutputRegular<Driver>,
  defaultOptions: CreateRendererOptions = {},
) {
  const createDriver = ({
    rendered,
    element,
    dataHook,
  }: DriverFactoryWrapperArgs) =>
    driverFactory({
      element: element!,
      wrapper: rendered.container,
      eventTrigger: fireEvent,
      dataHook: dataHook!,
    });
  return createRendererBase<Driver>(createDriver, defaultOptions);
}

function driverFactoryWrapper<Driver extends BaseUniDriver>(
  driverFactory: TestkitOutputUni<Driver>,
) {
  return ({ element, dataHook }: DriverFactoryWrapperArgs) =>
    driverFactory(
      // non-null/undef assertion used here because of wrong external types, element CAN be null here
      jsdomReactUniDriver(element!),
      jsdomReactUniDriver(document.body),
      // non-null/undef assertion used here because of wrong external types, dataHook is optional
      { dataHook: dataHook! },
    );
}

/**
 * Creates a `render` function that returns the same object as `@testing-library/react`'s render, but
 * with and extra `driver` property which is a Unidriver.
 *
 * The returned render function arguments:
 * @param [React.Element] jsx a jsx element to render
 * @param [object] options - render-options for @testing-library/react. The options may also contain a `dataHook` prop which if provided then the driver would be created with the element which is found by the dataHook. If not provided then it assumes that the rendered root element is the component's root element and it will be used for the driver.
 */
export function createRendererWithUniDriver<Driver extends BaseUniDriver>(
  driverFactory: TestkitOutputUni<Driver>,
  defaultOptions: CreateRendererOptions = {},
) {
  const createDriver = driverFactoryWrapper(driverFactory);

  return createRendererBase<Driver>(createDriver, defaultOptions);
}

/**
 * Creates a `render` function that returns Promise of the same object as `@testing-library/react`'s render, but
 * with and extra `driver` property which is a Unidriver. Internally wraps 'render' with async 'act', to settle the async state updates after component renders.
 *
 * The returned render function arguments:
 * @param [React.Element] jsx a jsx element to render
 * @param [object] options - render-options for @testing-library/react. The options may also contain a `dataHook` prop which if provided then the driver would be created with the element which is found by the dataHook. If not provided then it assumes that the rendered root element is the component's root element and it will be used for the driver.
 */
export function createAsyncRendererWithUniDriver<Driver extends BaseUniDriver>(
  driverFactory: TestkitOutputUni<Driver>,
  defaultOptions: CreateRendererOptions = {},
) {
  const createDriver = driverFactoryWrapper(driverFactory);

  return createAsyncRendererBase<Driver>(createDriver, defaultOptions);
}

export * from '@testing-library/react';
export * from './runAbsolutelyAllTimers';
